import { useQuery } from '@tanstack/react-query';
import { baseCoin } from '@/lib/symbol';
import { resolveSpelling } from '@/lib/assetName.core';

/*
 * ASSET TYPES AND BROKER AVAILABILITY (backend rest/instrument_registry.go,
 * 2026-10-04). Guillaume: "create Asset-Type on CopyFi (Crypto, Stock,
 * Commodity) next to the assets, maybe also remove the USDT from every pair",
 * and filter by type and by whether the asset is listed on a broker: "some
 * traders are really good but they are biased because their top trades are on
 * some assets not available on Phemex".
 *
 * One register for every symbol CopyFi stores, loaded once and cached: its
 * name without the quote currency, its type, and the brokers listing it. A
 * symbol the register does not know (it is rebuilt every six hours) falls back
 * to its own name and to crypto, the type of everything listed before TradFi.
 */

export type AssetClass = 'crypto' | 'stock' | 'commodity' | 'indicator' | 'forex';
export type Broker = 'phemex' | 'kucoin';

export const ASSET_CLASSES: AssetClass[] = ['crypto', 'stock', 'commodity', 'indicator', 'forex'];
/** The types offered in menus: forex is typed and kept, not offered yet (Guillaume, 2026-10-04). */
export const SHOWN_ASSET_CLASSES: AssetClass[] = ['crypto', 'stock', 'commodity'];

/** A saved type choice the switch no longer offers (the retired "Market
 *  indicators", folded into Stocks/ETFs and Crypto on 2026-10-04) reads as all. */
export const shownClass = (v: AssetClass | 'all'): AssetClass | 'all' =>
  v === 'all' || SHOWN_ASSET_CLASSES.includes(v) ? v : 'all';
export const shownFilter = (f: AssetFilter): AssetFilter =>
  shownClass(f.assetClass) === f.assetClass ? f : { ...f, assetClass: 'all' };

export type InstrumentInfo = {
  symbol: string;
  canonical: string;
  display: string;
  class: AssetClass;
  brokers: Partial<Record<Broker, boolean>>;
  /** Where the asset behind it trades (backend markethours): absent never closes; US, CME, KRX, NSE, FX. */
  market?: string;
  /** What it is, where that says more than the ticker: Gold for XAU (2026-10-06). */
  name?: string;
};

export type MarketStatus = { market: string; name: string; open: boolean; next_change: string };

/** Which markets are open now (/api/v1/market-hours), refreshed each minute. */
export function useMarketHours() {
  return useQuery<Map<string, MarketStatus>>({
    queryKey: ['market-hours'],
    queryFn: async () => {
      const r = await fetch('/api/v1/market-hours');
      if (!r.ok) throw new Error(`market-hours: ${r.status}`);
      const body = (await r.json()) as { markets?: MarketStatus[] };
      return new Map((body.markets ?? []).map((m) => [m.market, m]));
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
}

/** asset: one asset's slug (2026-10-06), the leaderboard ranked on that asset alone. */
export type AssetFilter = { assetClass: AssetClass | 'all'; broker: Broker | 'any'; asset?: string };
export const NO_ASSET_FILTER: AssetFilter = { assetClass: 'all', broker: 'any' };

export const assetFilterActive = (f: AssetFilter | undefined): boolean =>
  !!f && (f.assetClass !== 'all' || f.broker !== 'any' || !!f.asset);

/** The filter as query parameters for the dashboard endpoints ('' when off). */
export function assetFilterQuery(f: AssetFilter | undefined): string {
  if (!f || !assetFilterActive(f)) return '';
  return `${f.assetClass !== 'all' ? `&asset_class=${f.assetClass}` : ''}${f.broker !== 'any' ? `&broker=${f.broker}` : ''}${f.asset ? `&asset=${encodeURIComponent(f.asset)}` : ''}`;
}

/*
 * A cache key element for a FILTERED list, appended after the venue only when
 * a filter is on. The socket writes live updates into keys whose last element
 * is the venue (useWebSocket inVenue): an unfiltered list keeps them, and a
 * filtered list is never overwritten with unfiltered rows.
 */
export function assetFilterKey(f: AssetFilter | undefined): string[] {
  return f && assetFilterActive(f) ? [`asset:${f.assetClass}|${f.broker}${f.asset ? `|${f.asset}` : ''}`] : [];
}

const COMMODITY = new Set(['XAU', 'GOLD', 'XAG', 'SILVER', 'XPT', 'PLATINUM', 'XPD', 'PALLADIUM', 'CL', 'XTI', 'WTI', 'USOIL',
  'BZ', 'XBR', 'BRENTOIL', 'UKOIL', 'NATGAS', 'NG', 'NGAS', 'COPPER', 'XCU']);
const FOREX = new Set(['EUR', 'JPY', 'GBP', 'CHF', 'AUD', 'CAD', 'NZD', 'CNH', 'MXN', 'KRW']);
// Same list as the backend's indicatorNames (exchange/asset_class.go).
const INDICATOR = new Set(['DXY', 'USDX', 'US500', 'SP500', 'SPX', 'US30', 'DJI', 'DOW', 'NAS100', 'USTECH', 'XYZ100', 'NDX',
  'SMALL2000', 'RUSSELL2000', 'US2000', 'USBOND', 'VIX', 'TOTAL', 'TOTAL2', 'TOTAL3', 'OTHERS', 'BTCD', 'BTCDOM', 'DEFI',
  'JP225', 'DE40', 'UK100', 'HK50']);
const CRYPTO_INDICATOR = new Set(['TOTAL', 'TOTAL2', 'TOTAL3', 'OTHERS', 'BTCD', 'BTCDOM', 'DEFI']);

/** An index contract by its ticker (the backend's indicatorNames). */
export const isIndexName = (display: string): boolean => INDICATOR.has(display.toUpperCase());

/** What the register would say about a symbol it has not seen. */
export function fallbackInstrument(symbol: string): InstrumentInfo {
  const s = String(symbol ?? '').toUpperCase();
  const display = baseCoin(s.replace(/USDTM$/, 'USDT')) || s;
  // Indexes show with stocks/ETFs, crypto-market aggregates with crypto (backend IndicatorClass).
  const cls: AssetClass = INDICATOR.has(display) ? (CRYPTO_INDICATOR.has(display) ? 'crypto' : 'stock')
    : FOREX.has(display) ? 'forex' : COMMODITY.has(display) ? 'commodity' : 'crypto';
  return { symbol: s, canonical: s, display, class: cls, brokers: {} };
}

type Register = { bySymbol: Map<string, InstrumentInfo>; builtAt: number };

/** The register, loaded once per ten minutes and shared by every screen. */
export function useInstruments() {
  return useQuery<Register>({
    queryKey: ['instruments'],
    queryFn: async () => {
      const r = await fetch('/api/v1/instruments');
      if (!r.ok) throw new Error(`instruments: ${r.status}`);
      const body = (await r.json()) as { instruments?: InstrumentInfo[]; built_at?: number };
      const bySymbol = new Map<string, InstrumentInfo>();
      for (const i of body.instruments ?? []) bySymbol.set(i.symbol.toUpperCase(), i);
      return { bySymbol, builtAt: body.built_at ?? 0 };
    },
    staleTime: 10 * 60_000,
    gcTime: 60 * 60_000,
    refetchOnWindowFocus: false,
    retry: 2,
  });
}

/**
 * A symbol's register entry. With the trader's id, a name that is another
 * instrument on the trader's venue resolves to that one: MEXC's CATUSDT is a
 * memecoin, filed "CATUSDT@MEXC", while Binance's is Caterpillar (2026-10-05,
 * exchange.VenueSpelling on the server).
 */
export function instrumentOf(symbol: string | null | undefined, reg?: Register, traderUid?: string | null): InstrumentInfo {
  const s = String(symbol ?? '').toUpperCase().trim();
  const uid = String(traderUid ?? '');
  const venue = uid.startsWith('mexc:') ? 'MEXC' : uid.startsWith('hl:') ? 'HYPERLIQUID' : '';
  // The name adjuster (lib/assetName.core.ts): any venue spelling to its asset.
  const { hit, clean } = resolveSpelling(s, reg?.bySymbol, venue);
  if (hit) return hit;
  return { ...fallbackInstrument(clean || s), symbol: s };
}

/** An asset's name as a person reads it (SNDK, never SNDKUSDTM), from the one register. */
export function assetDisplay(symbol: string | null | undefined, reg?: Register, traderUid?: string | null): string {
  if (!symbol) return '';
  return instrumentOf(symbol, reg, traderUid).display;
}

/** The name adjuster as a function: (symbol, traderUid?) -> "SNDK". For text built in code. */
export function useAssetName(): (symbol: string | null | undefined, traderUid?: string | null) => string {
  const { data } = useInstruments();
  return (symbol, traderUid) => assetDisplay(symbol, data, traderUid);
}

/** A symbol's register entry, for one row or cell. */
export function useInstrument(symbol: string | null | undefined): InstrumentInfo {
  const { data } = useInstruments();
  return instrumentOf(symbol, data);
}

/** Does a symbol pass an asset-type / broker filter? Unknown availability counts as not listed. */
export function matchesAssetFilter(symbol: string | null | undefined, f: AssetFilter | undefined, reg?: Register, traderUid?: string | null): boolean {
  if (!f || !assetFilterActive(f)) return true;
  const i = instrumentOf(symbol, reg, traderUid);
  if (f.assetClass !== 'all' && i.class !== f.assetClass) return false;
  if (f.broker !== 'any' && !i.brokers[f.broker]) return false;
  return true;
}
